import Icons from "@/assets/icons";
import Images from "@/assets/images";
import { ActionButton } from "@/components/ActionButton";
import LottieConfetti from "@/components/LottieConfetti";
import { FontFamilies } from "@/constants/Typography";
import FBTrackingService from "@/services/fbTrackingService";
import useFinanceDataStore from "@/store/financeData";
import {
  formatCentsToDisplay,
  parseDisplayToCents,
  handleCurrencyInputChange,
} from "@/utils/myMoney";
import { Image } from "expo-image";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import {
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Ionicons } from "@expo/vector-icons";
import { SCREEN_WIDTH } from "@/constants/Sizes";

const SetBudgetScreen = () => {
  const { financeData, updateBudget } = useFinanceDataStore();
  const [isComplete, setIsComplete] = useState(false);
  const [budgetValue, setBudgetValue] = useState("$0");
  const [isSaving, setIsSaving] = useState(false);

  // Calculate savings based on income and budget
  const incomeCents = financeData?.analysis?.income?.totalMonthly || 0;
  const budgetCents = parseDisplayToCents(budgetValue);
  const savingsCents = Math.max(0, incomeCents - budgetCents);
  const savingsAmount = formatCentsToDisplay(savingsCents);

  // Initialize budget from store, default to 80% of income
  useEffect(() => {
    if (financeData?.analysis?.totalBudget) {
      setBudgetValue(formatCentsToDisplay(financeData.analysis.totalBudget));
    } else if (incomeCents > 0) {
      setBudgetValue(formatCentsToDisplay(Math.round(incomeCents * 0.8)));
    }
  }, [financeData]);

  useEffect(() => {
    FBTrackingService.trackViewContent("my-money-set-budget");
  }, []);

  const handleContinue = async () => {
    if (isComplete) {
      router.dismissTo("/(tabs)/my-money");
      return;
    }

    // Save budget via API
    setIsSaving(true);
    const success = await updateBudget(budgetCents);
    setIsSaving(false);

    if (success) {
      // Show completion screen
      setIsComplete(true);
      FBTrackingService.trackViewContent("my-money-budget-complete");
    }
  };

  const handleBack = () => {
    router.back();
  };

  const handleBudgetChange = (text: string) => {
    setBudgetValue(handleCurrencyInputChange(text));
  };

  // Completion Mode
  if (isComplete) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.content}>
          <LottieConfetti active={isComplete} />

          {/* Header with Logo */}
          <View style={styles.header}>
            <Icons.LogoWithTextBlack width={142} height={34} />
          </View>

          {/* Content */}
          <View style={styles.completeContent}>
            {/* Coin Image */}
            <View style={styles.coinContainer}>
              <Image source={Images.coin3d} style={styles.coinImage} />
            </View>

            {/* Savings Text */}
            <Text style={styles.savingsLabel}>
              If you follow this budget,{"\n"}you&apos;ll save
            </Text>

            {/* Amount */}
            <Text style={styles.savingsAmount}>{savingsAmount}</Text>
            <Text style={styles.savingsPeriod}>this month</Text>

            {/* Estimate Note */}
            <Text style={styles.estimateNote}>
              This estimate is based on your income
            </Text>
          </View>

          {/* Continue Button */}
          <View style={styles.buttonContainer}>
            <ActionButton
              title="Continue"
              type="primary"
              onPress={handleContinue}
              style={styles.continueButton}
            />
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // Budget Input Mode (default)
  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.content}>
        <ScrollView
          style={styles.inputScrollView}
          contentContainerStyle={styles.inputScrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.inputContent}>
            {/* Back Button */}
            <TouchableOpacity style={styles.backButton} onPress={handleBack}>
              <Ionicons name="chevron-back" size={28} color="#161D14" />
            </TouchableOpacity>

            {/* Title and Description */}
            <View style={styles.inputHeader}>
              <Text style={styles.inputTitle}>Set Total Budget</Text>
              <Text style={styles.inputSubtitle}>
                Enter the amount you want to spend this month. You can adjust
                category allocations later.
              </Text>
            </View>

            {/* Input Field */}
            <View style={styles.budgetInputContainer}>
              <TextInput
                style={styles.budgetInput}
                value={budgetValue}
                onChangeText={handleBudgetChange}
                keyboardType="decimal-pad"
                selection={{
                  start: budgetValue.length,
                  end: budgetValue.length,
                }}
              />
            </View>

            {/* Savings Preview */}
            {incomeCents > 0 && (
              <View style={styles.savingsPreview}>
                <Text style={styles.savingsPreviewText}>
                  Estimated savings: {savingsAmount}/month
                </Text>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Continue Button */}
        <View style={styles.buttonContainer}>
          <ActionButton
            title={isSaving ? "" : "Continue"}
            type="primary"
            onPress={handleContinue}
            style={styles.continueButton}
            disabled={isSaving}
          >
            {isSaving && <ActivityIndicator color="#FFFFFF" />}
          </ActionButton>
        </View>
      </View>
    </SafeAreaView>
  );
};

export default SetBudgetScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  content: {
    flex: 1,
    maxWidth: SCREEN_WIDTH,
    alignSelf: "center",
  },
  header: {
    alignItems: "center",
    paddingTop: 16,
    paddingBottom: 24,
  },
  buttonContainer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  continueButton: {
    width: "100%",
    height: 56,
    borderRadius: 100,
  },
  // Input mode styles
  inputScrollView: {
    flex: 1,
  },
  inputScrollContent: {
    flexGrow: 1,
  },
  inputContent: {
    flex: 1,
    paddingHorizontal: 16,
    gap: 28,
  },
  backButton: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
  },
  inputHeader: {
    gap: 8,
  },
  inputTitle: {
    fontSize: 30,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 37,
  },
  inputSubtitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.7,
    lineHeight: 23,
  },
  budgetInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#161D14",
  },
  budgetInput: {
    flex: 1,
    fontSize: 24,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 31,
    padding: 0,
  },
  savingsPreview: {
    backgroundColor: "#ECFDF5",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  savingsPreviewText: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#0F9129",
  },
  // Complete mode styles
  completeContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
    gap: 8,
  },
  coinContainer: {
    marginBottom: 16,
  },
  coinImage: {
    width: 100,
    height: 100,
  },
  savingsLabel: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 26,
  },
  savingsAmount: {
    fontSize: 48,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 52,
    marginTop: 8,
  },
  savingsPeriod: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 24,
  },
  estimateNote: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    textAlign: "center",
    lineHeight: 24,
    marginTop: 24,
  },
});
